import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { IonButton } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Subject, catchError, combineLatest, distinctUntilChanged, firstValueFrom, map, of, switchMap } from 'rxjs';
import { MonitoringPreferencesService } from '../../../core/services/monitoring-preferences.service';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { MonitoringSelection } from '../../models/be/monitoring-preferences';

@Component({
  selector: 'app-monitoring-preferences',
  imports: [CommonModule, FormsModule, TranslateModule, MatFormFieldModule, MatSelectModule, IonButton],
  templateUrl: './monitoring-preferences.component.html',
  styleUrl: './monitoring-preferences.component.scss',
  host: { '[class.mobile-settings]': 'mobile' }
})
export class MonitoringPreferencesComponent implements OnInit {
  @Input() mobile = false;
  @Input() showSaveButton = false;
  @Input() set selectedBuildingId(value: string | null | undefined) { this.contextBuildingId$.next(value); }
  @Output() readonly selectionSaved = new EventEmitter<MonitoringSelection>();
  private readonly preferences = inject(MonitoringPreferencesService);
  private readonly requests = inject(RequestsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadSelection$ = new Subject<{ buildingId: string; notice?: string }>();
  private readonly contextBuildingId$ = new BehaviorSubject<string | null | undefined>(undefined);
  private contextGeneration = 0;
  buildingId = '';
  selection: MonitoringSelection | null = null;
  draft = new Set<string>();
  loading = false;
  saving = false;
  error = '';
  notice = '';
  saved = false;

  ngOnInit(): void {
    this.loadSelection$.pipe(
      switchMap(({ buildingId, notice }) => {
        this.selection = null; this.draft = new Set(); this.error = ''; this.saved = false;
        this.notice = notice || ''; this.loading = !!buildingId;
        return buildingId ? this.preferences.load(buildingId).pipe(
          catchError(error => { this.error = this.errorKey(error); return of(null); })
        ) : of(null);
      }), takeUntilDestroyed(this.destroyRef)
    ).subscribe(selection => {
      this.selection = selection;
      this.draft = new Set(selection?.selectedUnitIds || []);
      this.loading = false;
    });
    combineLatest([this.requests.currentUser$, this.contextBuildingId$]).pipe(
      map(([user, selected]) => {
        const complete = user?.role !== 'superuser' || !!(user.customer?.id && user.organization?.id && user.building?.id);
        const buildingId = user?.id ? (selected !== undefined ? selected || '' : complete ? user.building?.id || '' : '') : '';
        return { key: JSON.stringify([user?.id, user?.role, String(user?.customer?.id ?? ''), user?.organization?.id, buildingId]), buildingId };
      }), distinctUntilChanged((previous, current) => previous.key === current.key), takeUntilDestroyed(this.destroyRef)
    ).subscribe(context => {
      this.contextGeneration++;
      this.buildingId = context.buildingId;
      this.loadSelection$.next({ buildingId: this.buildingId });
    });
    this.preferences.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event.buildingId === this.buildingId && !this.saving
        && (event.version === undefined || event.version !== this.selection?.version)) {
        this.loadSelection$.next({ buildingId: this.buildingId, notice: 'CONFLICT' });
      }
    });
    this.preferences.snapshots$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(selection => {
      if (selection.buildingId !== this.buildingId || !this.selection || this.loading || this.saving) return;
      if (selection.version !== this.selection.version || selection.state !== this.selection.state
        || JSON.stringify(selection.selectedUnitIds) !== JSON.stringify(this.selection.selectedUnitIds)) {
        this.draft = new Set(selection.selectedUnitIds);
        this.notice = 'CONFLICT'; this.saved = false;
      }
      this.selection = selection;
    });
  }

  selectBuilding(): void { this.loadSelection$.next({ buildingId: this.buildingId }); }

  get selectedUnitIds(): string[] { return [...this.draft]; }
  get disabled(): boolean { return !this.buildingId || this.loading || this.saving || !this.selection || this.selection.state === 'NO_ACCESSIBLE_UNITS'; }

  selectUnits(unitIds: string[]): void {
    if (this.disabled || !unitIds.length) return;
    this.draft = new Set(unitIds);
    this.error = ''; this.saved = false;
  }

  get dirty(): boolean {
    const selected = this.selection?.selectedUnitIds || [];
    return this.draft.size !== selected.length || selected.some(id => !this.draft.has(id));
  }

  get canSave(): boolean {
    return !!this.selection && this.selection.state !== 'NO_ACCESSIBLE_UNITS'
      && !this.loading && !this.saving && this.draft.size > 0 && this.dirty;
  }

  async saveIfNeeded(): Promise<boolean> {
    if (this.saving || this.loading) return false;
    if (this.selection?.state === 'SELECTION_REQUIRED' && !this.draft.size) {
      this.error = 'MINIMUM';
      return false;
    }
    return this.dirty ? this.save() : true;
  }

  async save(): Promise<boolean> {
    if (!this.canSave || !this.selection) return false;
    const buildingId = this.buildingId;
    const generation = this.contextGeneration;
    this.saving = true; this.error = ''; this.saved = false;
    try {
      const selection = await firstValueFrom(this.preferences.save(buildingId, this.selection.version, [...this.draft]).pipe(
        takeUntilDestroyed(this.destroyRef)
      ));
      if (generation !== this.contextGeneration) return false;
      this.selection = selection; this.draft = new Set(selection.selectedUnitIds);
      this.notice = ''; this.saved = true;
      this.selectionSaved.emit(selection);
      return true;
    } catch (error) {
      if (generation !== this.contextGeneration) return false;
      const errorKey = this.errorKey(error);
      if (error instanceof HttpErrorResponse && error.status === 409) {
        this.loadSelection$.next({ buildingId, notice: 'CONFLICT' });
      } else {
        this.error = errorKey;
      }
      return false;
    } finally { this.saving = false; }
  }

  retry(): void {
    if (this.buildingId) this.selectBuilding();
  }

  private errorKey(error: unknown): string {
    const response = error as { status?: number; error?: { error?: string } };
    if (response.status === 403) return 'FORBIDDEN';
    if (response.error?.error === 'MONITORING_SELECTION_EMPTY') return 'MINIMUM';
    if (['MONITORING_UNITS_INVALID', 'MONITORING_UNITS_INVALID_OR_FORBIDDEN'].includes(response.error?.error || '')) return 'INVALID_UNITS';
    return this.saving ? 'SAVE_ERROR' : 'LOAD_ERROR';
  }
}
